import InvoicePage from "./InvoiceGenerator/components/InvoicePage";
import { Invoice } from "./InvoiceGenerator/data/types";
import { useEffect, useState } from "react";
import { Box, Button, CircularProgress } from "@mui/material";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { doc, updateDoc, getDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { pdf } from "@react-pdf/renderer";
import { auth, db, storage } from "../firebase";

export interface EditInvoiceContext {
  invoiceId: string;
  type: "purchase" | "sales" | "expense";
  originalInvoice: any;
  invoiceNumber: number;
}

function InvoiceGenerator() {
  const savedInvoice = window.localStorage.getItem("invoiceData");
  const editContext = window.localStorage.getItem("editInvoiceContext");
  const navigate = useNavigate();
  const [isEditMode, setIsEditMode] = useState(false);
  const [context, setContext] = useState<EditInvoiceContext | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [useStock, setUseStock] = useState(false);
  const [loadingSettings, setLoadingSettings] = useState(true);
  let data: Invoice | undefined = undefined;

  useEffect(() => {
    // Load user settings
    const loadUserSettings = async () => {
      const user = auth.currentUser;
      if (!user) {
        setLoadingSettings(false);
        return;
      }

      try {
        const userDoc = await getDoc(doc(db, "users", user.uid));
        if (userDoc.exists()) {
          setUseStock(userDoc.data().useStock ?? false);
        }
      } catch (error) {
        console.error("Error loading user settings:", error);
      } finally {
        setLoadingSettings(false);
      }
    };

    loadUserSettings();
  }, []);

  useEffect(() => {
    if (editContext) {
      try {
        const ctx = JSON.parse(editContext);
        setIsEditMode(true);
        setContext(ctx);
      } catch (_e) {
        console.error("Error parsing edit context");
      }
    }
  }, [editContext]);

  try {
    if (savedInvoice) {
      data = JSON.parse(savedInvoice);
    }
  } catch (_e) {}

  const onInvoiceUpdated = (invoice: Invoice) => {
    window.localStorage.setItem("invoiceData", JSON.stringify(invoice));
  };

  const handleSaveEdit = async () => {
    if (!context) return;

    // Read the freshest edited data directly from localStorage, since InvoicePage
    // manages edits in its own local state and only persists them via onChange
    let invoice: Invoice;
    try {
      const latest = window.localStorage.getItem("invoiceData");
      if (!latest) return;
      invoice = JSON.parse(latest);
    } catch (_e) {
      Swal.fire("Error", "Could not read invoice data", "error");
      return;
    }

    setIsSaving(true);
    try {
      const user = auth.currentUser;
      if (!user) {
        Swal.fire("Error", "No user logged in", "error");
        setIsSaving(false);
        return;
      }

      const userId = user.uid;
      const collectionName =
        context.type === "purchase"
          ? "purchaseInvoices"
          : context.type === "sales"
            ? "salesInvoices"
            : "expenseInvoices";

      // Calculate totals from invoice data
      const productLines = invoice.productLines || [];
      const subtotal = productLines.reduce((sum: number, line: any) => {
        const quantity = parseFloat(line.quantity) || 0;
        const rate = parseFloat(line.rate) || 0;
        return sum + quantity * rate;
      }, 0);

      const vatTotal = subtotal * 0.18;
      const total = subtotal + vatTotal;

      // Regenerate the PDF with the edited data and override the stored file
      let fileUrl = context.originalInvoice?.fileUrl;
      const fileName = context.originalInvoice?.fileName;
      if (fileName) {
        try {
          const pdfDoc = <InvoicePage pdfMode={true} data={invoice} />;
          const pdfBlob = await pdf(pdfDoc).toBlob();
          const storageRef = ref(
            storage,
            `${collectionName}/${userId}/${fileName}`,
          );
          await uploadBytes(storageRef, pdfBlob);
          fileUrl = await getDownloadURL(storageRef);
        } catch (pdfError) {
          console.error("Error regenerating PDF:", pdfError);
        }
      }

      // Update Firestore with new invoice data
      const invoiceDoc = doc(
        db,
        collectionName,
        userId,
        "userInvoices",
        context.invoiceId,
      );

      await updateDoc(invoiceDoc, {
        // Keep original fields
        name: invoice.name || `Invoice_${context.invoiceNumber}`,
        invoiceNumber: context.invoiceNumber, // Keep the same invoice number
        clientName: invoice.clientName || "Unknown",
        // Update calculated fields
        subtotal: parseFloat(subtotal.toFixed(2)),
        vatTotal: parseFloat(vatTotal.toFixed(2)),
        total: parseFloat(total.toFixed(2)),
        // Updated PDF file reference
        fileUrl,
        // Store the full invoice data as JSON for reference
        invoiceData: JSON.stringify(invoice),
      });

      // Clear the edit context from localStorage
      window.localStorage.removeItem("editInvoiceContext");
      window.localStorage.removeItem("invoiceData");

      Swal.fire("Success", "Invoice updated successfully", "success").then(
        () => {
          // Navigate back to the appropriate invoice list
          navigate(
            context.type === "purchase"
              ? "/purchases"
              : context.type === "sales"
                ? "/sales"
                : "/expenses",
          );
        },
      );
    } catch (error) {
      console.error("Error saving invoice:", error);
      Swal.fire("Error", "Failed to save invoice", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    // Clear the edit context from localStorage
    window.localStorage.removeItem("editInvoiceContext");
    window.localStorage.removeItem("invoiceData");

    // Navigate back to the appropriate invoice list
    if (context) {
      navigate(
        context.type === "purchase"
          ? "/purchases"
          : context.type === "sales"
            ? "/sales"
            : "/expenses",
      );
    } else {
      navigate("/");
    }
  };

  if (loadingSettings) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div className="invoiceGenerator">
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <h1 className="app__title">
          {isEditMode ? "Edit Invoice" : "Invoice Generator"}
        </h1>
        {isEditMode && (
          <Box>
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleCancel}
              disabled={isSaving}
              sx={{ mr: 1 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => handleSaveEdit()}
              disabled={isSaving || !data}
            >
              {isSaving ? <CircularProgress size={24} /> : "Save Invoice"}
            </Button>
          </Box>
        )}
      </Box>
      <InvoicePage
        key={context?.invoiceId ?? "new"}
        data={data}
        onChange={onInvoiceUpdated}
        useStock={useStock}
        isEditMode={isEditMode}
      />
    </div>
  );
}

export default InvoiceGenerator;
